import React, { useState } from 'react';
import {
  User,
  Package,
  ClipboardList,
  FileText,
  HandCoins,
  Receipt,
  Landmark,
  FileSpreadsheet,
  Handshake,
} from 'lucide-react';

type IconComponent = React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;

type QA = {
  id: string;
  label: string;
  description: string;
  vela?: boolean;
  color: string;
  Icon: IconComponent;
};

// 9 quick actions. All icons teal (#15A08C) except Contract which uses the
// Vela purple (#A855F7). Each has a short description shown as a dark
// hover tooltip above the icon. No pending count badges: notifications now
// live only in the Vela brief card at the top of the Overview.
const ACTIONS: QA[] = [
  { id: 'customer',  label: 'Customer',           description: 'Add a new customer to your records',           color: '#15A08C', Icon: User            },
  { id: 'product',   label: 'Product or service', description: 'Add something you sell',                       color: '#15A08C', Icon: Package         },
  { id: 'estimate',  label: 'Estimate',           description: 'Create an estimate to send to a prospect',     color: '#15A08C', Icon: ClipboardList   },
  { id: 'invoice',   label: 'Invoice',            description: 'Bill a customer for work done or goods sold', color: '#15A08C', Icon: FileText        },
  { id: 'payment',   label: 'Receive payment',    description: 'Record a payment against an open invoice',    color: '#15A08C', Icon: HandCoins       },
  { id: 'receipt',   label: 'Sales receipt',      description: 'Record a cash or card sale paid at the time', color: '#15A08C', Icon: Receipt         },
  { id: 'deposit',   label: 'Bank deposit',       description: 'Record money moved into your bank account',   color: '#15A08C', Icon: Landmark        },
  { id: 'statement', label: 'Create statement',   description: 'Send a customer a summary of what they owe',  color: '#15A08C', Icon: FileSpreadsheet },
  { id: 'contract',  label: 'Contract',           description: 'Draft a service contract with Vela',          color: '#A855F7', Icon: Handshake, vela: true },
];

const CARD: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E7EAF0',
  borderRadius: 10,
  padding: '16px 18px',
  marginBottom: 14,
};
const HEAD: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const TITLE: React.CSSProperties = { fontSize: 14, fontWeight: 700, margin: 0, color: '#0E1A1A' };
const LEGEND: React.CSSProperties = { display: 'flex', gap: 12, fontSize: 10.5, color: '#66748B' };
const LEG_ITEM: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5 };
const LEG_PLUS: React.CSSProperties = {
  width: 10, height: 10, background: '#0E1A1A', color: '#FFFFFF', borderRadius: '50%',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 8, fontWeight: 900, lineHeight: 1,
};
const LEG_RING: React.CSSProperties = {
  width: 10, height: 10, borderRadius: '50%',
  background: 'conic-gradient(from 0deg, #8B5CF6, #6B46C1, #8B5CF6)',
};

// Sage-tinted inner panel that groups the Money in label and the icon grid.
// Visible margin on the right so the panel ends well before the outer card edge.
const INNER_PANEL: React.CSSProperties = {
  background: '#EDF2EE',
  borderRadius: 10,
  padding: '14px 18px 20px',
  margin: '4px 16px 4px 4px',
};

const SECTION_LBL: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 800, color: '#66748B',
  letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 14,
};
const GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  columnGap: 10,
  rowGap: 20,
  alignItems: 'start',
};
const WRAP: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  gap: 6, cursor: 'pointer', padding: '4px 0', position: 'relative',
};
const CIRCLE_OUTER: React.CSSProperties = {
  position: 'relative', width: 52, height: 52, borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const CIRCLE_INNER: React.CSSProperties = {
  width: 52, height: 52, background: '#FFFFFF', borderRadius: '50%',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const CIRCLE_INNER_VELA: React.CSSProperties = { ...CIRCLE_INNER, width: 48, height: 48 };
const VELA_RING: React.CSSProperties = {
  position: 'absolute', inset: 0, borderRadius: '50%',
  background: 'conic-gradient(from 0deg, #8B5CF6, #6B46C1, #8B5CF6, #6B46C1)',
  padding: 2,
};
const PLUS_BADGE: React.CSSProperties = {
  position: 'absolute', bottom: -2, right: -2,
  width: 16, height: 16, background: '#0E1A1A', color: '#FFFFFF',
  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: 800, lineHeight: 1,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)', zIndex: 3,
};
const LABEL: React.CSSProperties = {
  fontSize: 11, color: '#0E1A1A', textAlign: 'center', lineHeight: 1.25, maxWidth: 84,
};

// Dark hover tooltip rendered above the icon when the user hovers the action.
const TOOLTIP: React.CSSProperties = {
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#0E1A1A',
  color: '#FFFFFF',
  fontSize: 11,
  lineHeight: 1.35,
  padding: '7px 10px',
  borderRadius: 7,
  width: 'max-content',
  maxWidth: 200,
  textAlign: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  zIndex: 100,
  pointerEvents: 'none',
};
const TOOLTIP_ARROW: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '5px solid transparent',
  borderRight: '5px solid transparent',
  borderTop: '5px solid #0E1A1A',
};

function ActionItem({ a }: { a: QA }): React.ReactElement {
  const [hovered, setHovered] = useState(false);
  const Icon = a.Icon;
  return (
    <div
      style={WRAP}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={CIRCLE_OUTER}>
        {a.vela && <div style={VELA_RING} />}
        <div style={a.vela ? CIRCLE_INNER_VELA : CIRCLE_INNER}>
          <Icon size={26} color={a.color} strokeWidth={2} />
        </div>
        <span style={PLUS_BADGE}>+</span>
      </div>
      <div style={LABEL}>{a.label}</div>
      {hovered && (
        <div style={TOOLTIP} role="tooltip">
          {a.description}
          <div style={TOOLTIP_ARROW} />
        </div>
      )}
    </div>
  );
}

export default function QuickActions() {
  return (
    <div style={CARD}>
      <div style={HEAD}>
        <h3 style={TITLE}>Quick actions</h3>
        <div style={LEGEND}>
          <span style={LEG_ITEM}><span style={LEG_PLUS}>+</span> create</span>
          <span style={LEG_ITEM}><span style={LEG_RING} /> Vela</span>
        </div>
      </div>
      <div style={INNER_PANEL}>
        <div style={SECTION_LBL}>Money in</div>
        <div style={GRID}>
          {ACTIONS.map((a) => <ActionItem key={a.id} a={a} />)}
        </div>
      </div>
    </div>
  );
}
